// GENERATED from messaging-platform/packages/chat-core/src/hub-types.ts. Do not edit here: edit there, then run scripts/sync-chat-core.cjs.
/**
 * The messaging hub's wire shapes, as hub-api sends them (messaging-platform/apps/api/src/conversations.ts, rpc.ts).
 * Framework-free on purpose: this folder (`chat/core`) is meant to be lifted into a shared package when MedicAI
 * Connect moves onto the hub (tasks/messaging-hub-architecture.md M5), so nothing here may import Angular.
 */

export type ConversationType = 'channel' | 'direct' | 'group_dm' | 'patient' | 'referral' | 'support' | 'network' | 'patient_direct' | 'billing';

/** What the composer offers in a conversation: @ for colleagues, # for patients. */
export interface ComposeFeatures {
  people: boolean;
  patients: boolean;
}

/**
 * Per conversation type (owner, 2026-09-21), the same rule the hub enforces (hub-api conversations.ts composePolicy):
 * support takes neither (MedicAI Support are not colleagues, and patients are not support's to hold); a referral chat
 * takes colleagues but no patient links (it is about ONE patient, with someone outside the practice); a network 1-on-1
 * (2026-09-22) takes patient links (copied into the sender's own practice only) and has nobody else to @-mention; a
 * patient's own conversation with the practice (patient_direct) takes neither; the rest take both, billing chats
 * (2026-10-05) included: people on both sides, and a # copies into both sides' patient chats.
 */
export function composeFeatures(type: ConversationType): ComposeFeatures {
  return {
    people: type !== 'support' && type !== 'network' && type !== 'patient_direct',
    patients: type !== 'support' && type !== 'referral' && type !== 'patient_direct',
  };
}

/** The placeholder's tail for what the composer offers: " · @ for a colleague · # for a patient", or less, or nothing. */
export function composeHint(features: ComposeFeatures): string {
  return (features.people ? ' · @ for a colleague' : '') + (features.patients ? ' · # for a patient' : '');
}
export type AccessMode = 'participant' | 'admin' | 'public_member' | 'org_member';
export type NotifyLevel = 'all' | 'mentions' | 'none';

/** A device registration for push (M4): a web push subscription, or a native device token. */
export interface PushRegistration {
  installationId: string;
  platform: 'web' | 'apns' | 'fcm';
  channel: { endpoint: string; p256dh: string; auth: string } | { token: string };
}

export interface HubOrg {
  orgId: string;
  /** the org's id in this app: the clinical group id */
  ref: string;
  role: 'admin' | 'member';
  /** the org's name, as the app that vouched for it calls it */
  name?: string;
}

export interface HubReady {
  principalId: string;
  displayName: string;
  issuer: string;
  subject: string;
  orgs: HubOrg[];
  /** profile photos: one ticket for every photo this page shows, good for an hour (hub avatars.ts) */
  avatarTicket?: string;
  /** this person's own photo, if they have one (2026-09-22): member lists leave the viewer out, so it is sent here */
  avatar?: string;
}

export interface ConversationSummary {
  id: string;
  type: ConversationType;
  orgId: string;
  visibility: 'public' | 'private' | null;
  name: string | null;
  description: string | null;
  lastMessageSeq: number | null;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  /** when the conversation was made: how a room nobody has written in yet is still ordered */
  createdAt?: string;
  archivedAt: string | null;
  access: AccessMode;
  myRole: 'owner' | 'admin' | 'member' | null;
  lastReadSeq: number;
  unread: number;
  mutedUntil: string | null;
  /** when I pinned it to the top of my list (at most five per practice); null or absent when not pinned */
  pinnedAt?: string | null;
  /** which messages push to my devices (M4): all, only @-mentions, or none */
  notifyLevel?: NotifyLevel;
  members?: { principalId: string; name: string; avatar?: string }[];
  /** patient chats: the patient's id in the app that owns them, and which app that is (absent = clinical) */
  patientRef?: string;
  patientSystem?: 'clinical' | 'connect';
  /** why nobody can post any more (a referral chat whose sharing stopped or link ended); absent or null = writable */
  readOnlyReason?: string | null;
  /** referral chats (M6): the link, and this viewer's side of it */
  referral?: ReferralView;
  /** network 1-on-1s (2026-09-22): who is on the other end of the referral network link, as this side names them */
  network?: NetworkView;
  /** a patient's own conversation with a practice (messaging-platform docs/patient-chat.md) */
  patientDirect?: PatientDirectView;
  /** billing chats (2026-10-05): the room, a 1:1 or a group between a billing team on Connect and a practice on clinical */
  billing?: BillingView;
  /** a group's photo: its ref (pair with ChatStore.conversationAvatarUrl); absent when it has none */
  avatar?: string;
  /**
   * Ticks (2026-09-20): how far EVERY OTHER participant has got, from the hub. A message of seq <= deliveredSeq is on
   * all their devices; <= readSeq they have all read it. `others` is how many other participants there are.
   */
  receipts?: { deliveredSeq: number; readSeq: number; others: number };
  /** in-app support (M7): the practice user who asked ('requester') or MedicAI Support ('agent') */
  support?: { side: 'requester' | 'agent' };
}

export interface ReferralView {
  linkRef: string;
  /** the referring practice's patient: with the link, how the referrals dashboard knows the patient */
  ownerPatientRef: string;
  side: 'owner' | 'receiver' | 'external';
  /** this side's own patient (a practice side), null for the external clinician */
  patientRef: string | null;
  /** who is on the other end, as this side's app names them */
  counterpart: string | null;
  /**
   * Files may be sent here: only when both ends are MedicAI practices (owner, 2026-09-21). A practice and a clinician
   * outside MedicAI exchange text only; the hub refuses a file with EXTERNAL_REFERRAL_NO_FILES. Absent from a hub that
   * predates it: treat as false.
   */
  filesAllowed?: boolean;
  /**
   * Files may be sent here with leave from this app, one message at a time (2026-09-29): a chat with a clinician outside
   * MedicAI, where each message with files spends one of the referral's documents. The store asks the app for that
   * leave (setFileGrantProvider) before sending; the hub refuses such a message without it (FILE_GRANT_REQUIRED).
   */
  filesByGrant?: boolean;
}

/**
 * A network 1-on-1 (2026-09-22): a practice's clinician and a clinician on the other end of one of its referral network
 * links (a doctor of a linked MedicAI practice, or the external clinician). `linkRef` is that link: with a patient, it
 * says whether the patient has been referred to them.
 */
/** A billing chat as this viewer's side sees it: which kind, their side, and the org on the other side. */
export interface BillingView {
  kind: 'room' | 'direct' | 'group';
  /** 'billing': the billing team on Connect; 'practice': the practice on clinical */
  side: 'billing' | 'practice';
  counterpartOrgId: string;
  counterpartName: string | null;
}

/** A billing relation (a billing team on Connect bills a practice on clinical), as one of its sides sees it. */
export interface BillingRelation {
  orgId: string;
  otherOrgId: string;
  otherName: string | null;
  side: 'billing' | 'practice';
  /** the pair's standing room */
  roomId: string;
  /** ended: its chats are read-only, history kept */
  ended: boolean;
}

/** Someone on the other side of a billing relation, with their clinical user id when they have one. */
export interface BillingPerson {
  principalId: string;
  name: string;
  role: string;
  avatar?: string;
  clinicalUserId?: string;
}

export interface NetworkView {
  counterpart: { name: string | null; practiceName: string | null; kind: 'practice' | 'external'; linkRef: string };
  /** files only between two MedicAI practices, as in a referral chat */
  filesAllowed: boolean;
}

/**
 * A patient's conversation with a practice: the viewer's side, whether it is a 12-17 year old's own (guardians never see
 * it), and how each side is named. Only the practice side learns the practice's patient id and the profile.
 */
export interface PatientDirectView {
  side: 'patient' | 'practice';
  audience: 'family' | 'minor';
  practiceName: string | null;
  patientName: string | null;
  patientRef?: string;
  profileRef?: string;
}

/**
 * Whether a file may be attached in this conversation: always, except a referral or network chat that is not
 * practice-to-practice, and a patient's conversation (text only for now).
 */
export function conversationAcceptsFiles(c: { type: string; referral?: { filesAllowed?: boolean; filesByGrant?: boolean }; network?: { filesAllowed?: boolean } }): boolean {
  if (c.type === 'patient_direct') return false;
  if (c.type === 'network') return c.network?.filesAllowed === true;
  return c.type !== 'referral' || c.referral?.filesAllowed === true || c.referral?.filesByGrant === true;
}

/** A patient chat's activity per day, for the patient timeline. */
export interface PatientChatActivity {
  conversationId: string | null;
  /** `referralCount`: how many of `count` came from referral chats (M6; the record's copy only) */
  days: { date: string; count: number; lastAt: string; referralCount?: number }[];
}

export interface MessageView {
  id: string;
  seq: number;
  conversationId: string;
  kind: 'user' | 'system' | 'linked_copy' | 'forwarded' | 'action';
  sender: { principalId: string; name: string; avatar?: string } | null;
  body: string | null;
  /** principals: @-mentioned people; patients: #-mentioned patient refs (each linked into that patient's chat) */
  mentions: { principals: string[]; patients?: string[] };
  replyToMessageId: string | null;
  /** where the message this one answers sits in the conversation, so a click can open it there */
  replyToSeq?: number;
  editedAt: string | null;
  deletedAt: string | null;
  createdAt: string;
  reactions: { emoji: string; principalIds: string[] }[];
  /** files sent with it (M5) */
  attachments?: AttachmentView[];
  /** in-app support: an agent's request for the user's app logs, or the user's answer to one */
  logRequest?: LogRequestView;
  /** a frozen copy in a patient chat (§8.4): where it came from, and what became of the original */
  linkedFrom?: LinkedFrom;
  /** the patient chats this message was linked into */
  linkedTo?: LinkedTo[];
  /** a forwarded message: where it was forwarded from, and who wrote it there (the sender is who forwarded it) */
  forwardedFrom?: ForwardedFrom;
  /** an action message: what was made from the chat (gone once the message is deleted) */
  card?: ActionCard;
}

/** What an action message made from the chat (a task for a patient), for the chat to draw and open. */
export interface ActionCard {
  type: 'task' | 'appointment' | 'note';
  recordId: string;
  patientRef: string;
  patientName: string;
  title: string;
  /**
   * Kept up to date by the clinical platform as the record changes. A task: open (when absent) or done. A booking:
   * booked, confirmed, arrived, completed, cancelled or did not attend.
   */
  status?: 'open' | 'done' | AppointmentStatus;
  /** who marked it done (a task) or last changed it (a booking) */
  doneBy?: string;
  doneAt?: string;
  /** the assignees' names */
  assignees?: string[];
  /** the assignees' clinical user ids, for "my tasks" */
  assigneeIds?: string[];
  /** YYYY-MM-DD */
  due?: string;
  /** a booking: the practice's local day and time (YYYY-MM-DDTHH:mm), its end (HH:mm) and doctor */
  start?: string;
  end?: string;
  doctor?: string;
  /** a note: its type as the practice names it ("Ward round") */
  noteType?: string;
}

export type AppointmentStatus = 'booked' | 'confirmed' | 'arrived' | 'completed' | 'cancelled' | 'did_not_attend';

/**
 * Where a forwarded message came from (2026-10-02). As with a linked copy, `messageId`, `conversationId` and `seq` are
 * null for a reader who may not open the original (it is in a conversation they are not part of): the label only.
 */
export interface ForwardedFrom {
  messageId: string | null;
  conversationId: string | null;
  seq: number | null;
  /** 'the Theatre Team group', a patient chat's name, 'a direct message', 'a patient-app chat', 'a referral chat', ... */
  label: string;
  /** who wrote the original */
  originalSender: { principalId: string; name: string } | null;
  originalCreatedAt: string;
  originalDeleted: boolean;
}

/** A pinned message (2026-10-02): up to three per conversation, shown to everyone who reads it. */
export interface PinView {
  messageId: string;
  seq: number;
  pinnedBy: { principalId: string; name: string };
  pinnedAt: string;
  senderName: string;
  /** a short line to show: the text, the file's name, or 'Photo' */
  preview: string;
}

/** What a conversation is being filed to: every new message is copied into these patient chats until it is ended. */
export interface FilingView {
  patients: { conversationId: string; patientRef: string | null; name: string | null }[];
  startedBy: { principalId: string; name: string };
  startedAt: string;
}

export interface LinkedFrom {
  /**
   * Where the original is. Null for a reader who may not open it: a copy from a private conversation (a DM, a group DM,
   * a network chat) points back only for that conversation's own people; everyone else gets the label only.
   */
  messageId: string | null;
  conversationId: string | null;
  /** where the original sits in ITS conversation, so "go back to it" can open there (`openAt`) */
  seq: number | null;
  /** '#channel', a patient chat's name, or 'a direct message' */
  label: string;
  linkedBy: { principalId: string; name: string };
  linkedAt: string;
  editedSinceLink: boolean;
  originalDeleted: boolean;
  /** set once the copy was taken off the patient's chat, filed there by mistake (2026-10-23); the copy is then deleted */
  unlinkedBy?: { principalId: string; name: string };
  unlinkedAt?: string;
}

/** Before unlinking: how many later messages were filed to the patient with this one, and whether filing goes on. */
export interface UnlinkScope {
  later: number;
  filing: boolean;
}

export interface LinkedTo {
  conversationId: string;
  name: string | null;
  patientRef: string | null;
  /** the practice whose patient chat it is (a network chat shows each side only its own practice's links) */
  orgId?: string;
  /**
   * 'referral' (2026-09-22): a network chat's message linked into the REFERRAL chat about the patient
   * (msg:linkReferral), by the external clinician or by the practice once it has referred the patient over this link.
   * Both sides see it. The name is the viewer's own app's (null until that app has named the chat); `patientRef` is
   * set for the practice side only.
   */
  kind?: 'referral';
  /** that referral chat is read-only now (sharing stopped or the link ended): show the chip as unavailable */
  unavailable?: boolean;
  /** a referral link's chat key (`linkRef:ownerPatientRef` is how the referrals dashboard knows the patient) */
  referralKey?: { linkRef: string; ownerPatientRef: string };
}

/** A patient named with `#` in a message being sent: linked as soon as the hub stores the message. */
export interface PatientMention {
  patientRef: string;
  title: string;
  /** whose patient (M5): the clinical platform's (the default), or MedicAI Connect's for a tenant with no practice behind it */
  system?: 'clinical' | 'connect';
}

/** A message in the client: the server's view, or one of ours still on its way (or failed). */
export interface ThreadMessage extends MessageView {
  pending?: boolean;
  failed?: boolean;
  /** our own id for a send, so a retry is stored once and the ack replaces the placeholder */
  clientMessageId?: string;
  /** the # mentions of a send still on its way, so a retry links the same patients */
  patientMentions?: PatientMention[];
}

export interface MemberView {
  principalId: string;
  name: string;
  role: string;
  online?: boolean;
  /** the version of their profile photo, when they have one (see ChatStore.avatarUrl) */
  avatar?: string;
}

export interface HubEvent {
  event: string;
  data: unknown;
}

/** What the store needs from a connection; the real one is HubConnection, tests use a fake. */
/** One person's ticks in a conversation ("read by 3 of 4"). */
export interface ConversationReceipt {
  principalId: string;
  name: string;
  deliveredSeq: number;
  readSeq: number;
  readAt: string | null;
}

/** One search result (2026-09-20): enough to list it, and to open the conversation at that message. */
export interface SearchHit {
  messageId: string;
  conversationId: string;
  conversationName: string;
  conversationType: ConversationType;
  seq: number;
  createdAt: string;
  senderName: string;
  snippet: string;
}

/** What a sender sees on their own message. */
export type MessageTick = 'sending' | 'failed' | 'sent' | 'delivered' | 'read';

export interface HubTransport {
  request<T>(event: string, payload?: unknown): Promise<T>;
  onEvent(listener: (e: HubEvent) => void): () => void;
  onReady(listener: (ready: HubReady) => void): () => void;
  /** the hub's base URL (files are uploaded to and fetched from it over HTTP); null before the first connect */
  baseUrl?(): string | null;
}

/**
 * A log request in in-app support (2026-09-21), on both of its messages: the agent's REQUEST carries the current status
 * (the user's app shows it as a prompt while 'pending'); the user's RESPONSE carries the answer it gave.
 */
export interface LogRequestView {
  id: string;
  role: 'request' | 'response';
  status: 'pending' | 'accepted' | 'declined';
}

/** A file sent with a message (M5). Fetch it with ChatStore.fileUrl: the URL is short-lived. */
export interface AttachmentView {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
}

/** A hub error code (NOT_FOUND, FORBIDDEN, READ_ONLY, RATE_LIMITED, BAD_REQUEST, ...), carried as the message. */
export class HubRequestError extends Error {
  constructor(readonly code: string) {
    super(code);
    this.name = 'HubRequestError';
  }
}
